<script setup lang="ts">
import {
  AtomImage,
  AtomLoadingSpinner,
  AtomTextarea,
  MolFormControl,
  MolButtonMoney,
  SatCounter,
  useToast,
  SatIconButton,
  useImage,
} from "@chatfood/design-system";
import { bemBuilder, scrollToId } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import type { IItemViewEmits } from "./emits";
import type { ModifierList } from "./types";
import { useLocale } from "~/composables/use-locale";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import {
  ItemHandler,
  type IItemHandlerProps,
  type IItemHandlerEmits,
  type IItemHandlerSelected,
} from "~/components/item-handler";
import type { IDomainItem } from "~/domain/item";
import { ItemCombo, type IItemComboEmits } from "~/components/item-combo";
import { useAnalytics, useBugReporter, watch } from "#imports";
import { useWindowResize } from "~/composables/use-window-resize";
import { useItemStore } from "~/stores/item";
import { useAddItemBasketErrorValidation } from "~/composables/use-add-item-basket-error-validation";

const props = defineProps<{
  menuId: string;
}>();

const emits = defineEmits<IItemViewEmits>();

const { t } = useLocale();
const { isMobile } = useWindowResize();

const css = bemBuilder("item-modal");

const { toastError } = useToast();
const { optimize } = useImage();
const { report } = useBugReporter();

const business = useBusinessStore();
const basket = useBasketStore();
const featureMatrix = useFeatureMatrixStore();
const itemStore = useItemStore();

const isLoading = computed(() => {
  return itemStore.isProcessing;
});

const discountPercentage = computed(() => {
  const discount = basket.discount;
  let percentage = 0;

  if (
    itemStore.isLoaded &&
    discount &&
    discount.unit === "percentage" &&
    (discount.discountedProducts.length === 0 ||
      discount.discountedProducts.includes(itemStore.plu))
  ) {
    percentage = discount.amount;
  }

  return percentage;
});

const item = computed<IDomainItem | undefined>(() => {
  if (discountPercentage.value === 0) return itemStore.state;

  return {
    ...itemStore,
    originalPrice: itemStore.price,
    price: {
      currency: itemStore.price.currency,
      value: itemStore.price.value * (1 - discountPercentage.value / 100),
    },
    modifierLists: itemStore.modifierLists.map((modifierList) => ({
      ...modifierList,
      modifiers: modifierList.modifiers.map((item) => ({
        ...item,
        modifierLists: item.modifierLists.map((itemModifierList) => ({
          ...itemModifierList,
          modifiers: itemModifierList.modifiers.map((itemModifierListItem) => ({
            ...itemModifierListItem,
            originalPrice: itemModifierListItem.price,
            price: {
              currency: itemModifierListItem.price.currency,
              value:
                itemModifierListItem.price.value *
                (1 - discountPercentage.value / 100),
            },
          })),
        })),
        originalPrice: item.price,
        price: {
          currency: item.price.currency,
          value: item.price.value * (1 - discountPercentage.value / 100),
        },
      })),
    })),
  };
});

const definePreSelection = (
  modifierLists: IDomainItem["modifierLists"],
  parentModifier?: IItemHandlerSelected,
) => {
  modifierLists.forEach((modifierList) => {
    modifierList?.modifiers?.forEach((modifier) => {
      if (modifier.defaultQuantity > 0) {
        const newModifierItem: IItemHandlerSelected = {
          id: modifier.id,
          modifierListId: modifierList.id,
          name: modifier.name,
          price: modifier.price,
          quantity: modifier.defaultQuantity,
          items: [],
        };

        if (parentModifier) {
          parentModifier?.items?.push(newModifierItem);
        } else {
          const existingModifierList = modifierListSelected.value.find(
            (modifierSelected) => modifierSelected.id === modifierList.id,
          );

          if (existingModifierList) {
            existingModifierList.items.push(newModifierItem);
          } else {
            modifierListSelected.value.push({
              id: modifierList.id,
              items: [newModifierItem],
            });
          }
        }

        if (modifier.modifierLists?.length) {
          definePreSelection(modifier.modifierLists, newModifierItem);
        }
      }
    });
  });
};

watch(
  () => itemStore.isLoaded,
  (value) => {
    if (!value) return;
    definePreSelection(itemStore.modifierLists);
  },
  {
    once: true,
  },
);

const quantity = ref(1);

const modifierListSelected = ref<IItemHandlerProps["modifierListSelected"]>([]);

function onModifierListChange(payload: IItemHandlerEmits["change"][0]) {
  const isItemSelectedBeforeUpdate = findItemSelected(
    payload.modifierListId,
    payload.itemId,
  );

  modifierListSelected.value = payload.modifierListSelected;

  if (!isItemSelectedBeforeUpdate) {
    handleCombo(payload.modifierListId, payload.itemId);
  }
}

function findModifierList(modifierListId: string) {
  return item.value?.modifierLists.find(
    (modifierList) => modifierList.id === modifierListId,
  );
}

function findItemWithCombo(modifierListId: string, itemId: string) {
  const modifierList = findModifierList(modifierListId);

  return modifierList?.modifiers?.find(
    (item) => item.id === itemId && item.modifierLists.length,
  );
}

function findModifierListSelected(modifierListId: string) {
  return modifierListSelected.value.find(
    (modifierList) => modifierList.id === modifierListId,
  );
}

function findItemSelected(modifierListId: string, itemId: string) {
  return findModifierListSelected(modifierListId)?.items.find(
    (item) => item.id === itemId,
  );
}

function updateQuantity(amount: number) {
  if (isLoading.value || isAdding.value) return;

  quantity.value = amount;
}

const itemsSelectedTotalPrice = computed(() => {
  return modifierListSelected.value.reduce(
    (acc, cur) => acc + calculateItemsPrice(cur.items),
    0,
  );
});

function calculateItemsPrice(items: IItemHandlerSelected[]): number {
  return items.reduce((total, item) => {
    const itemPrice = item.price.value;
    const subItemsPrice = item.items ? calculateItemsPrice(item.items) : 0;
    const sumPrices = (itemPrice + subItemsPrice) * item.quantity;
    return total + sumPrices;
  }, 0);
}

// sum item price + modifiers * quantity
const calculatePrice = computed(() => {
  if (!item.value) return 0;

  const subtotal = item.value.price.value + itemsSelectedTotalPrice.value;
  return subtotal * quantity.value;
});

const invalidModifierLists = ref<string[]>([]);
const handleValidate = (payload: IItemHandlerEmits["validate"][0]) => {
  invalidModifierLists.value = invalidModifierLists.value.filter(
    (id) => id !== payload.categoryId,
  );

  if (!payload.valid) {
    invalidModifierLists.value.push(payload.categoryId);
  }
};

const invalidCombos = computed(() => {
  const items = modifierListSelected.value
    .filter((obj) => obj.items.length)
    .flatMap((obj) => obj.items);

  const filterRequireds = items.filter(
    (obj) => minSelections(obj.modifierListId, obj.id) > 0,
  );

  return filterRequireds.map((obj) => `${obj.modifierListId}-${obj.id}`);
});

function minSelections(modifierListId: string, itemId: string) {
  const item = findItemWithCombo(modifierListId, itemId);
  if (!item?.modifierLists?.length) return 0;

  return item.modifierLists.reduce(
    (acc, cur) =>
      cur.minSelection > checkQuantity(modifierListId, itemId, cur.id)
        ? acc + 1
        : acc,
    0,
  );
}

function checkQuantity(
  modifierListId: string,
  itemId: string,
  modifierListCheck: string,
) {
  const itemSelected = findItemSelected(modifierListId, itemId);
  if (!itemSelected?.items?.length) return 0;

  return itemSelected?.items.reduce(
    (acc, cur) =>
      cur.modifierListId === modifierListCheck ? acc + cur.quantity : acc,
    0,
  );
}

const note = ref("");

const isAdding = ref(false);

const { trackEvent } = useAnalytics();
const { handleBasketError } = useAddItemBasketErrorValidation();

async function onAddToBasket() {
  // check if there are modifier lists required
  if (Boolean(invalidModifierLists.value.length) || !item.value) {
    return scrollToId(invalidModifierLists.value[0]);
  }

  // check if there are item combos required
  if (invalidCombos.value.length) {
    return scrollToId(invalidCombos.value[0]);
  }

  isAdding.value = true;

  try {
    await basket.addItem({
      id: item.value.id,
      unitPrice: item.value.price.value,
      quantity: quantity.value,
      menuId: props.menuId,
      notes: note.value,
      modifierLists: modifierListSelected.value.map((modifierList) => ({
        id: modifierList.id,
        modifiers: modifierList.items.map((item) => ({
          id: item.id,
          unitPrice: item.price.value,
          quantity: item.quantity,
          modifierLists: parseComboItems(item.items),
        })),
      })),
    });

    trackEvent({
      name: "addItemToBasket",
      payload: {
        id: item.value.id,
        name: item.value.name,
        quantity: quantity.value,
        price: item.value.originalPrice ?? item.value.price,
      },
    });

    function parseComboItems(items: IItemHandlerSelected[] = []) {
      return items.reduce((acc: ModifierList[], item: IItemHandlerSelected) => {
        const existingItem = acc.find(
          (modifierList) => modifierList.id === item.modifierListId,
        );

        if (existingItem) {
          existingItem.modifiers.push({
            ...item,
            ...{ unitPrice: item.price.value },
          });
        } else {
          acc.push({
            id: item.modifierListId,
            modifiers: [{ ...item, ...{ unitPrice: item.price.value } }],
          });
        }

        return acc;
      }, []);
    }

    onCloseModal();
    onClearData();
  } catch (error) {
    const wasHandled = handleBasketError(
      error,
      item.value.modifierLists,
      item.value.id,
    );

    if (wasHandled) return;

    report(error);
    toastError(t("components.item_modal.item_add_error_message"));
  } finally {
    isAdding.value = false;
  }
}

function onClearData() {
  itemStore.reset();
  modifierListSelected.value = [];
  quantity.value = 1;
  note.value = "";
  invalidModifierLists.value = [];
  onClearCombo();
}

const onCloseModal = () => {
  if (comboSelected.value) {
    onClearCombo();
    return;
  }

  emits("close");
};

function onCustomizeModifier(
  payload: IItemHandlerEmits["customizeModifier"][0],
) {
  handleCombo(payload.categoryId, payload.itemId);
}

const comboSelected = ref();
function handleCombo(modifierListId: string, itemId: string) {
  const itemCombo = findItemWithCombo(modifierListId, itemId);

  if (itemCombo && item.value) {
    const itemSelected = findItemSelected(modifierListId, itemId);

    comboSelected.value = {
      itemName: item.value.name,
      modifierListId,
      itemsSelected: itemSelected?.items ?? [],
      item: itemCombo,
    };
  }
}

function onComboSelected(payload: IItemComboEmits["change"][0]) {
  const newModifierList = modifierListSelected.value.map((modifierList) => {
    const items = modifierList.items.map((item) => ({
      ...item,
      items: item.id === payload.itemId ? payload.itemsSelected : item.items,
    }));

    return {
      ...modifierList,
      ...(modifierList.id === payload.modifierListId ? { items } : {}),
    };
  });

  modifierListSelected.value = newModifierList;

  onClearCombo();
}

function onClearCombo() {
  comboSelected.value = undefined;
}
</script>

<template>
  <div
    :class="[
      css(''),
      { [css('', 'has-photo')]: item?.photo && !comboSelected },
    ]"
  >
    <AtomLoadingSpinner v-if="isLoading" :class="css('loading')" />
    <ItemCombo
      v-else-if="comboSelected"
      :item="comboSelected.item"
      :item-name="comboSelected.itemName"
      :modifier-list-id="comboSelected.modifierListId"
      :items-selected="comboSelected.itemsSelected"
      @close="onClearCombo"
      @change="onComboSelected"
    />
    <div v-else-if="item" :class="css('item-level')">
      <div :class="css('content')">
        <SatIconButton
          :class="css('close')"
          icon-name="close"
          size="medium"
          @click.prevent="onCloseModal"
        />
        <div v-if="item.photo">
          <AtomImage
            :class="css('photo')"
            :type="isMobile ? 'square' : 'rounded'"
            :size="600"
            :alt="item.name"
            :src="optimize(item.photo, { w: 600 })"
          />
        </div>
        <div :class="css('wrapper')">
          <ItemHandler
            :item="item"
            :modifier-list-selected="modifierListSelected"
            @change="onModifierListChange"
            @validate="handleValidate"
            @customize-modifier="onCustomizeModifier"
          />
          <div
            v-if="featureMatrix.isToggleEnabled('collect-item-notes')"
            data-test="notes"
            :class="css('note')"
          >
            <MolFormControl :label="t('components.item_modal.note_label')">
              <AtomTextarea
                :placeholder="t('components.item_modal.note_placeholder')"
                :auto-grow="true"
                :rows="2"
                :value="note"
                @change="(value) => (note = value)"
              />
            </MolFormControl>
          </div>
        </div>
      </div>
      <div :class="css('footer')">
        <SatCounter
          :count="quantity"
          :min-count="1"
          size="large"
          :show-clear="true"
          :clear-label="t('components.item_modal.remove_cta')"
          :show-delete="false"
          @change="updateQuantity"
        />
        <div :class="css('footer-cta')">
          <MolButtonMoney
            :disabled="!item.inStock || isAdding"
            :price="{
              value: calculatePrice,
              currency: item.price.currency,
            }"
            :locale="business.locale"
            :hide-cents="false"
            size="large"
            @click.prevent="onAddToBasket"
          >
            {{ t("components.item_modal.cta_label") }}
          </MolButtonMoney>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.item-modal {
  position: relative;
  display: flex;
  width: 580px;
  max-width: 100%;
  height: 100%;
  min-height: 30vh;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.2s ease-out;

  @media (width > $breakpoint-sm) {
    max-height: calc(100vh - 70px);
    overflow: auto;
  }

  &--has-photo {
    width: 864px;
  }

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &__item-level {
    display: flex;
    min-height: 100%;
    flex-direction: column;
  }

  &__content {
    flex: 1;
    position: relative;
    display: flex;
    height: auto;
    max-width: 100%;
    overflow: auto;
    flex-wrap: wrap;

    @media (width > $breakpoint-sm) {
      padding: var(--spacing-lg);
      gap: var(--spacing-lg) var(--spacing-2xl);

      > div {
        flex: 1;
      }
    }
  }

  &__wrapper {
    padding: var(--spacing-sm) var(--spacing-md);

    @media (width > $breakpoint-sm) {
      padding: 0;
    }
  }

  &__photo {
    width: 100%;

    @media (width > $breakpoint-sm) {
      position: sticky;
      top: 0;
    }
  }

  &__note {
    margin-top: var(--size-md);
    padding-top: var(--size-md);
    border-top: solid 1px var(--color-base-95);
  }

  &__close {
    position: absolute;
    inset-block-start: var(--spacing-md);
    inset-inline-end: var(--spacing-md);
    z-index: 1;

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: var(--size-md);
    padding: var(--spacing-md);
    justify-content: space-between;
    border-top: 1px solid var(--color-base-95);
    background-color: var(--color-base-100);
    position: sticky;
    bottom: 0;
    border-radius: 0 0 var(--radii-card-md) var(--radii-card-md);
  }

  &__footer-cta {
    width: 340px;
    max-width: 100%;
  }

  @media (width <= $breakpoint-sm) {
    &__content {
      flex-flow: column;
    }

    &__footer {
      flex-flow: column;
    }

    &__footer-cta {
      width: 100%;
    }
  }
}
</style>
